import React, { useState, useRef, useCallback } from 'react'
import ExcelJS from 'exceljs'
import JSZip from 'jszip'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  LinearProgress,
  Tooltip,
  Snackbar,
  Stack,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material'
import SecurityIcon from '@mui/icons-material/Security'
import CloudIcon from '@mui/icons-material/Cloud'
import StorageIcon from '@mui/icons-material/Storage'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DeleteIcon from '@mui/icons-material/Delete'
import InfoIcon from '@mui/icons-material/Info'
import ArticleIcon from '@mui/icons-material/Article'
import SendIcon from '@mui/icons-material/Send'
import RefreshIcon from '@mui/icons-material/Refresh'
import LockIcon from '@mui/icons-material/Lock'
import SearchIcon from '@mui/icons-material/Search'
import { certificateApi } from '../utils/api'

// ─── CSV Column Definitions ──────────────────────────────────────────────────

const AWS_COLUMNS = [
  { key: 'common_name',              label: 'common_name',              required: true,  description: 'Certificate common name (e.g. app.broadridge.com)' },
  { key: 'product_code',             label: 'product_code',             required: true,  description: 'ITSM product code' },
  { key: 'account_name',             label: 'account_name',             required: true,  description: 'AWS account name from onboarded accounts' },
  { key: 'account_number',           label: 'account_number',           required: true,  description: 'AWS account number (12-digit)' },
  { key: 'application_id',           label: 'application_id',           required: true,  description: 'Application UUID (brcp:application:id)' },
  { key: 'environment',              label: 'environment',              required: true,  description: 'SDLC environment: dev | qa | uat | prd' },
  { key: 'certificate_usage',        label: 'certificate_usage',        required: true,  description: 'server-internal | server-public | client-internal | client-broadridge | client-external | other' },
  { key: 'itsm_group',               label: 'itsm_group',               required: true,  description: 'ITSM assignment group of the team managing the cert (e.g. ICS-DT-CERT-MGMT)' },
  { key: 'venafi_policy_base',       label: 'venafi_policy_base',       required: true,  description: 'prod-digicert | prod-entrust | nonprod-digicert | nonprod-entrust' },
  { key: 'mutual_auth',              label: 'mutual_auth',              required: true,  description: 'yes | no' },
  { key: 'layer7',                   label: 'layer7',                   required: false, description: 'yes | no  (required only if mutual_auth=yes)' },
  { key: 'layer7_endpoint',          label: 'layer7_endpoint',          required: false, description: 'Required only if layer7=yes' },
  { key: 'client_name',              label: 'client_name',              required: false, description: 'Client name from Venafi (or leave blank)' },
  { key: 'dependency_present',       label: 'dependency_present',       required: true,  description: 'true | false' },
  { key: 'dependencies',             label: 'dependencies',             required: false, description: 'Pipe-separated list (required if dependency_present=true)' },
  { key: 'aws_cert_in_onprem',       label: 'aws_cert_in_onprem',       required: true,  description: 'yes | no — is cert also used on-premises?' },
  { key: 'acm_parameter',            label: 'acm_parameter',            required: true,  description: 'yes | no — store in ACM?' },
  { key: 'existing_acm_arn1',        label: 'existing_acm_arn1',        required: false, description: 'Existing ACM ARN #1 (only if acm_parameter=yes and cert already in ACM)' },
  { key: 'existing_acm_arn2',        label: 'existing_acm_arn2',        required: false, description: 'Existing ACM ARN #2 (only if acm_parameter=yes and a second region ARN exists)' },
  { key: 'ssm_parameter',            label: 'ssm_parameter',            required: true,  description: 'yes | no — store in SSM Parameter Store?' },
  { key: 'ssm_format',               label: 'ssm_format',               required: false, description: 'PEM | PFX  (required if ssm_parameter=yes)' },
  { key: 'ssm_cert_path',            label: 'ssm_cert_path',            required: false, description: 'SSM path for certificate (auto-filled from sdlc + common name if blank)' },
  { key: 'ssm_cert_content_type',    label: 'ssm_cert_content_type',    required: false, description: 'String | SecureString  (required if ssm_parameter=yes)' },
  { key: 'ssm_cert_param_type',      label: 'ssm_cert_param_type',      required: false, description: 'Standard | Advanced (PEM); Advanced only (PFX)' },
  { key: 'ssm_private_key_path',     label: 'ssm_private_key_path',     required: false, description: 'SSM path for private key (PEM only; auto-filled from common name if blank)' },
  { key: 'ssm_key_content_type',     label: 'ssm_key_content_type',     required: false, description: 'String | SecureString  (PEM only)' },
  { key: 'ssm_key_param_type',       label: 'ssm_key_param_type',       required: false, description: 'Standard | Advanced  (PEM only)' },
  { key: 'ssm_chain_key_path',       label: 'ssm_chain_key_path',       required: false, description: 'SSM path for chain (PEM only; auto-filled from common name if blank)' },
  { key: 'ssm_chain_content_type',   label: 'ssm_chain_content_type',   required: false, description: 'String | SecureString  (PEM only)' },
  { key: 'ssm_chain_param_type',     label: 'ssm_chain_param_type',     required: false, description: 'Advanced only (PEM only)' },
  { key: 'ssm_cert_password_path',   label: 'ssm_cert_password_path',   required: false, description: 'SSM path for password (PFX only; auto-filled from common name if blank)' },
  { key: 'ssm_password_content_type',label: 'ssm_password_content_type',required: false, description: 'String | SecureString  (PFX only)' },
  { key: 'ssm_password_param_type',  label: 'ssm_password_param_type',  required: false, description: 'Standard | Advanced  (PFX only)' },
  { key: 'sm_parameter',             label: 'sm_parameter',             required: true,  description: 'yes | no — store in Secrets Manager?' },
  { key: 'sm_format',                label: 'sm_format',                required: false, description: 'PEM | PFX | JKS  (required if sm_parameter=yes)' },
  { key: 'sm_cert_path',             label: 'sm_cert_path',             required: false, description: 'Secrets Manager secret name (e.g. /venafi/certificates/<sdlc>/<common-name>); auto-filled if blank' },
]

const ONPREM_COLUMNS = [
  { key: 'common_name',       label: 'common_name',       required: true,  description: 'Certificate common name (e.g. app.broadridge.com)' },
  { key: 'product_code',      label: 'product_code',      required: true,  description: 'ITSM product code' },
  { key: 'product_name',      label: 'product_name',      required: true,  description: 'ITSM product name for display' },
  { key: 'certificate_usage', label: 'certificate_usage', required: false, description: 'Override if needed: server-internal | server-public | client-internal | client-broadridge | client-external | other. Auto-populated from Venafi if blank.' },
  { key: 'itsm_group',         label: 'itsm_group',         required: true, description: 'ITSM assignment group of the team managing the cert (e.g. ICS-DT-CERT-MGMT)' },
]

// ─── Policy base detection from Venafi DN ────────────────────────────────────

const detectPolicyBase = (policyDn) => {
  if (!policyDn) return ''
  const dn = policyDn.toLowerCase()
  if (dn.includes('non-production\\brcc digicert standard ca'))      return 'nonprod-digicert'
  if (dn.includes('non-production\\brcc entrust private gateway ca')) return 'nonprod-entrust'
  if (dn.includes('\\production\\brcc digicert standard ca'))         return 'prod-digicert'
  if (dn.includes('\\production\\brcc entrust private gateway ca'))   return 'prod-entrust'
  return ''
}

// ─── Validation ──────────────────────────────────────────────────────────────

const VALID_ENVIRONMENTS  = ['dev', 'qa', 'uat', 'prd']
const VALID_CERT_USAGES   = ['server-internal', 'server-public', 'client-internal', 'client-broadridge', 'client-external', 'other']
const VALID_POLICY_BASES  = ['prod-digicert', 'prod-entrust', 'nonprod-digicert', 'nonprod-entrust']
const VALID_YES_NO        = ['yes', 'no']
const VALID_TRUE_FALSE    = ['true', 'false']
const VALID_SSM_FORMATS   = ['PEM', 'PFX']
const VALID_SM_FORMATS    = ['PEM', 'PFX', 'JKS']
const VALID_CONTENT_TYPES = ['String', 'SecureString']
const VALID_PARAM_TYPES   = ['Standard', 'Advanced']

// ─── Normalizer ─────────────────────────────────────────────────────────────
// Matches any input case to the canonical valid value; leaves free-text fields as-is.

const matchCanonical = (value, validValues) => {
  if (!value) return value
  const trimmed = String(value).trim()
  const found = validValues.find(v => v.toLowerCase() === trimmed.toLowerCase())
  return found !== undefined ? found : trimmed
}

const normalizeRow = (row) => {
  const r = { ...row }
  // Fields that must be lowercase
  const lcFields = [
    'mutual_auth', 'layer7', 'dependency_present', 'environment',
    'certificate_usage', 'venafi_policy_base',
    'aws_cert_in_onprem', 'acm_parameter', 'ssm_parameter', 'sm_parameter',
  ]
  lcFields.forEach(f => { if (r[f]) r[f] = String(r[f]).trim().toLowerCase() })

  // Fields with mixed/ProperCase valid values — match to canonical
  const canonicalFields = [
    { field: 'ssm_format',               valid: VALID_SSM_FORMATS   },
    { field: 'sm_format',                valid: VALID_SM_FORMATS    },
    { field: 'ssm_cert_content_type',    valid: VALID_CONTENT_TYPES },
    { field: 'ssm_cert_param_type',      valid: VALID_PARAM_TYPES   },
    { field: 'ssm_key_content_type',     valid: VALID_CONTENT_TYPES },
    { field: 'ssm_key_param_type',       valid: VALID_PARAM_TYPES   },
    { field: 'ssm_chain_content_type',   valid: VALID_CONTENT_TYPES },
    { field: 'ssm_chain_param_type',     valid: VALID_PARAM_TYPES   },
    { field: 'ssm_password_content_type',valid: VALID_CONTENT_TYPES },
    { field: 'ssm_password_param_type',  valid: VALID_PARAM_TYPES   },
  ]
  canonicalFields.forEach(({ field, valid }) => {
    if (r[field]) r[field] = matchCanonical(r[field], valid)
  })

  return r
}

const validateRow = (row, deploymentType) => {
  const errors = []
  const warnings = []
  const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS

  // Required field presence
  cols.filter(c => c.required).forEach(col => {
    if (!row[col.key] || String(row[col.key]).trim() === '') {
      errors.push(`"${col.key}" is required`)
    }
  })

  // For on-prem, only common_name/product_code/product_name are required in the CSV.
  // certificate_usage is the only optional override — validate it if provided.
  if (deploymentType === 'onprem') {
    if (row.certificate_usage && !VALID_CERT_USAGES.includes(row.certificate_usage))
      errors.push(`"certificate_usage" must be one of: ${VALID_CERT_USAGES.join(', ')}`)
    return { errors, warnings, isValid: errors.length === 0 }
  }

  // AWS validations (full)
  if (row.environment && !VALID_ENVIRONMENTS.includes(row.environment))
    errors.push(`"environment" must be one of: ${VALID_ENVIRONMENTS.join(', ')}`)

  if (row.certificate_usage && !VALID_CERT_USAGES.includes(row.certificate_usage))
    errors.push(`"certificate_usage" must be one of: ${VALID_CERT_USAGES.join(', ')}`)

  if (row.venafi_policy_base && !VALID_POLICY_BASES.includes(row.venafi_policy_base))
    errors.push(`"venafi_policy_base" must be one of: ${VALID_POLICY_BASES.join(', ')}`)

  if (row.mutual_auth && !VALID_YES_NO.includes(row.mutual_auth))
    errors.push('"mutual_auth" must be yes or no')

  if (row.dependency_present && !VALID_TRUE_FALSE.includes(row.dependency_present))
    errors.push('"dependency_present" must be true or false')

  // Conditional validations
  if (row.mutual_auth === 'yes' && !row.layer7)
    errors.push('"layer7" is required when mutual_auth=yes')

  if (row.layer7 === 'yes' && !row.layer7_endpoint)
    errors.push('"layer7_endpoint" is required when layer7=yes')

  if (row.dependency_present === 'true' && !row.dependencies)
    errors.push('"dependencies" required when dependency_present=true')

  if (row.ssm_parameter && !VALID_YES_NO.includes(row.ssm_parameter))
    errors.push('"ssm_parameter" must be yes or no')

  if (row.sm_parameter && !VALID_YES_NO.includes(row.sm_parameter))
    errors.push('"sm_parameter" must be yes or no')

  if (row.acm_parameter && !VALID_YES_NO.includes(row.acm_parameter))
    errors.push('"acm_parameter" must be yes or no')

  if (row.acm_parameter === 'yes') {
    const arn1 = row.existing_acm_arn1?.trim()
    const arn2 = row.existing_acm_arn2?.trim()
    if (!arn1 && !arn2)
      errors.push('"existing_acm_arn1" or "existing_acm_arn2": at least one ACM ARN is required when acm_parameter=yes')
  }

  if (row.aws_cert_in_onprem && !VALID_YES_NO.includes(row.aws_cert_in_onprem))
    errors.push('"aws_cert_in_onprem" must be yes or no')

  if (row.ssm_parameter === 'yes') {
    if (!row.ssm_format)
      errors.push('"ssm_format" required when ssm_parameter=yes')
    else if (!VALID_SSM_FORMATS.includes(row.ssm_format))
      errors.push(`"ssm_format" must be: ${VALID_SSM_FORMATS.join(', ')}`)

    // ssm_cert_content_type and ssm_cert_param_type are auto-defaulted; only validate if explicitly provided
    if (row.ssm_cert_content_type && !VALID_CONTENT_TYPES.includes(row.ssm_cert_content_type))
      errors.push(`"ssm_cert_content_type" must be: ${VALID_CONTENT_TYPES.join(', ')}`)

    if (row.ssm_cert_param_type && !VALID_PARAM_TYPES.includes(row.ssm_cert_param_type))
      errors.push(`"ssm_cert_param_type" must be: ${VALID_PARAM_TYPES.join(', ')}`)

    // PFX: cert param type must be Advanced only
    if (row.ssm_format === 'PFX' && row.ssm_cert_param_type && row.ssm_cert_param_type !== 'Advanced')
      errors.push('"ssm_cert_param_type" must be "Advanced" for PFX format')

    // PEM: chain param type must be Advanced only
    if (row.ssm_format === 'PEM' && row.ssm_chain_param_type && row.ssm_chain_param_type !== 'Advanced')
      errors.push('"ssm_chain_param_type" must be "Advanced" for PEM format')
  }

  if (row.sm_parameter === 'yes') {
    if (!row.sm_format)
      errors.push('"sm_format" required when sm_parameter=yes')
    else if (!VALID_SM_FORMATS.includes(row.sm_format))
      errors.push(`"sm_format" must be: ${VALID_SM_FORMATS.join(', ')}`)
  }

  // Warnings for empty optional fields
  if (!row.client_name)       warnings.push('"client_name" not provided (will auto-populate from Venafi)')

  return { errors, warnings, isValid: errors.length === 0 }
}

// Post-enrichment validation for on-prem rows (after Venafi data has been merged)
const validateRowPostEnrichment = (row) => {
  const errors = []
  const warnings = []

  // After Venafi enrichment, these fields should now be present
  if (!row.common_name)         errors.push('"common_name" is required')
  if (!row.product_code)        errors.push('"product_code" is required')
  if (!row.product_name)        errors.push('"product_name" is required')

  // Venafi-populated fields — warn if still missing
  if (!row.environment)         warnings.push('"environment" not available from Venafi — please verify')
  if (!row.certificate_usage)   warnings.push('"certificate_usage" not available from Venafi — please verify')
  if (!row.venafi_policy_base)  warnings.push('"venafi_policy_base" could not be detected — please verify')

  // Enum validations on enriched values
  if (row.environment && !VALID_ENVIRONMENTS.includes(row.environment))
    errors.push(`"environment" must be one of: ${VALID_ENVIRONMENTS.join(', ')}`)
  if (row.certificate_usage && !VALID_CERT_USAGES.includes(row.certificate_usage))
    errors.push(`"certificate_usage" must be one of: ${VALID_CERT_USAGES.join(', ')}`)
  if (row.venafi_policy_base && !VALID_POLICY_BASES.includes(row.venafi_policy_base))
    errors.push(`"venafi_policy_base" must be one of: ${VALID_POLICY_BASES.join(', ')}`)

  // Defaults already applied, but validate if present
  if (row.mutual_auth && !VALID_YES_NO.includes(row.mutual_auth))
    errors.push('"mutual_auth" must be yes or no')
  if (row.dependency_present && !VALID_TRUE_FALSE.includes(row.dependency_present))
    errors.push('"dependency_present" must be true or false')

  // Conditional
  if (row.mutual_auth === 'yes' && !row.layer7)
    errors.push('"layer7" is required when mutual_auth=yes')
  if (row.layer7 === 'yes' && !row.layer7_endpoint)
    errors.push('"layer7_endpoint" is required when layer7=yes')
  if (row.dependency_present === 'true' && !row.dependencies)
    errors.push('"dependencies" required when dependency_present=true')
  if (row.mutual_auth === 'yes') {
    if (!row.public_cert_key_location)
      errors.push('"public_cert_key_location" required when mutual_auth=yes')
    if (!row.private_cert_key_location)
      errors.push('"private_cert_key_location" required when mutual_auth=yes')
  }

  return { errors, warnings, isValid: errors.length === 0 }
}

// ─── CSV Parser ───────────────────────────────────────────────────────────────

const parseCSV = (text) => {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim() !== '')
  if (lines.length < 2) return { headers: [], rows: [] }

  const parseRow = (line) => {
    const result = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++ }
        else inQuotes = !inQuotes
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim())
        current = ''
      } else {
        current += ch
      }
    }
    result.push(current.trim())
    return result
  }

  const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'))
  const rows = lines.slice(1).map(line => {
    const values = parseRow(line)
    const obj = {}
    headers.forEach((h, i) => { obj[h] = values[i] || '' })
    return obj
  })

  return { headers, rows }
}

// ─── Dropdown definitions for XLSX data validation ──────────────────────────

const DROPDOWN_DEFS = {
  mutual_auth:              '"yes,no"',
  layer7:                   '"yes,no"',
  dependency_present:       '"true,false"',
  environment:              '"dev,qa,uat,prd"',
  certificate_usage:        '"server-internal,server-public,client-internal,client-broadridge,client-external,other"',
  venafi_policy_base:       '"prod-digicert,prod-entrust,nonprod-digicert,nonprod-entrust"',
  aws_cert_in_onprem:       '"yes,no"',
  acm_parameter:            '"yes,no"',
  ssm_parameter:            '"yes,no"',
  sm_parameter:             '"yes,no"',
  ssm_format:               '"PEM,PFX"',
  sm_format:                '"PEM,PFX,JKS"',
  ssm_cert_content_type:    '"String,SecureString"',
  ssm_cert_param_type:      '"Standard,Advanced"',
  ssm_key_content_type:     '"String,SecureString"',
  ssm_key_param_type:       '"Standard,Advanced"',
  ssm_chain_content_type:   '"String,SecureString"',
  ssm_chain_param_type:     '"Advanced"',
  ssm_password_content_type:'"String,SecureString"',
  ssm_password_param_type:  '"Standard,Advanced"',
}

// ─── Column index (1-based) → Excel letter (A, B, …, Z, AA, AB, …) ──────────
const colNumToLetter = (n) => {
  let s = ''
  while (n > 0) {
    const r = (n - 1) % 26
    s = String.fromCharCode(65 + r) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

// ─── Build raw XLSX dataValidations XML for injection ────────────────────────
// ExcelJS browser bundle strips dataValidation write support, so we inject
// the XML directly into the sheet ZIP entry using JSZip after generation.
const buildDataValidationsXml = (cols) => {
  const entries = cols
    .map((col, idx) => {
      const formula = DROPDOWN_DEFS[col.key]
      if (!formula) return null
      const colLetter = colNumToLetter(idx + 1)
      const sqref    = `${colLetter}2:${colLetter}1000`
      const errMsg   = formula.replace(/"/g, '')
      return (
        `<dataValidation type="list" allowBlank="1" showDropDown="0"` +
        ` showErrorMessage="1" errorStyle="warning"` +
        ` errorTitle="Suggested values" error="Valid options: ${errMsg}"` +
        ` sqref="${sqref}"><formula1>${formula}</formula1></dataValidation>`
      )
    })
    .filter(Boolean)
  if (entries.length === 0) return ''
  return `<dataValidations count="${entries.length}">${entries.join('')}</dataValidations>`
}

// ─── XLSX Template Generator ──────────────────────────────────────────────────

const AWS_SAMPLE_ROW = [
  'app.broadridge.com', 'BRCC-APP-001', 'brcc-app-prod-aws', '123456789012',
  'app-uuid-1234-abcd-5678', 'prd', 'server-internal', '', 'prod-digicert',
  'san1.broadridge.com|san2.broadridge.com', 'no', '', '', '',
  'false', '', 'no', 'yes', '', '', 'yes', 'PEM',
  '/venafi/certificates/app.broadridge.com/cert', 'SecureString', 'Standard',
  '/venafi/certificates/app.broadridge.com/key', 'SecureString', 'Standard',
  '/venafi/certificates/app.broadridge.com/chain', 'String', 'Standard',
  '', '', '', 'no', '', '', '', '', '',
]

const ONPREM_SAMPLE_ROW = [
  'app.broadridge.com', 'BRCC-APP-001', 'My Application', '', '',
]

const generateXLSXTemplate = async (deploymentType) => {
  const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
  const sampleValues = deploymentType === 'aws' ? AWS_SAMPLE_ROW : ONPREM_SAMPLE_ROW

  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'CertManage'
  const ws = workbook.addWorksheet('Certificates', {
    views: [{ state: 'frozen', ySplit: 1 }],
  })

  // ── Columns
  ws.columns = cols.map(c => ({
    header: c.key,
    key: c.key,
    width: Math.max(c.key.length + 4, 22),
  }))

  // ── Header row styling
  const headerRow = ws.getRow(1)
  headerRow.height = 28
  headerRow.eachCell((cell, colNum) => {
    const col = cols[colNum - 1]
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 }
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: col?.required ? 'FFC62828' : 'FF1565C0' },
    }
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false }
    cell.border = { bottom: { style: 'medium', color: { argb: 'FFFFFFFF' } } }
  })

  // ── Sample row (row 2)
  const sampleRow = ws.addRow(
    cols.reduce((obj, col, i) => { obj[col.key] = sampleValues[i] ?? ''; return obj }, {})
  )
  sampleRow.height = 20
  sampleRow.eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF9C4' } }
    cell.font = { italic: true, color: { argb: 'FF5D4037' }, size: 10 }
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFBBBBBB' } } }
    cell.alignment = { vertical: 'middle' }
  })

  // ── Instructions sheet
  const info = workbook.addWorksheet('Instructions')
  info.columns = [
    { header: 'Column', key: 'col', width: 30 },
    { header: 'Required', key: 'req', width: 12 },
    { header: 'Description', key: 'desc', width: 70 },
    { header: 'Valid Values / Dropdown', key: 'vals', width: 80 },
  ]
  const infoHeader = info.getRow(1)
  infoHeader.height = 22
  infoHeader.eachCell(cell => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF37474F' } }
    cell.alignment = { vertical: 'middle', horizontal: 'center' }
  })
  cols.forEach(c => {
    const row = info.addRow({
      col: c.key,
      req: c.required ? 'Required' : 'Optional',
      desc: c.description,
      vals: DROPDOWN_DEFS[c.key] ? DROPDOWN_DEFS[c.key].replace(/"/g, '') : '(free text)',
    })
    row.getCell('req').font = { color: { argb: c.required ? 'FFC62828' : 'FF546E7A' }, bold: c.required }
    row.getCell('col').font = { name: 'Courier New', size: 9 }
    row.height = 18
  })

  // ── Generate base XLSX buffer via ExcelJS (styling, columns, sample row)
  const baseBuffer = await workbook.xlsx.writeBuffer()

  // ── Inject dataValidations XML using JSZip (ExcelJS browser build omits this)
  // Per OOXML spec, <dataValidations> MUST appear immediately after </sheetData>
  const zip = await JSZip.loadAsync(baseBuffer)
  const sheetXml = await zip.file('xl/worksheets/sheet1.xml').async('string')
  const dvXml = buildDataValidationsXml(cols)
  const patchedXml = dvXml
    ? sheetXml.replace('</sheetData>', `</sheetData>${dvXml}`)
    : sheetXml
  zip.file('xl/worksheets/sheet1.xml', patchedXml)
  const finalBuffer = await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' })

  // ── Download
  const blob = new Blob([finalBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `bulk_upload_${deploymentType}_template.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ─── XLSX Parser ─────────────────────────────────────────────────────────────

const parseXLSXFile = async (file) => {
  const workbook = new ExcelJS.Workbook()
  const buffer = await file.arrayBuffer()
  await workbook.xlsx.load(buffer)
  const ws = workbook.worksheets[0]
  if (!ws) return { rows: [] }

  let headers = []
  const rows = []
  ws.eachRow((row, rowNumber) => {
    // row.values is 1-indexed; index 0 is always undefined in exceljs
    const values = row.values.slice(1)
    if (rowNumber === 1) {
      headers = values.map(v => String(v ?? '').trim().toLowerCase().replace(/\s+/g, '_'))
      return
    }
    // Skip completely empty rows and the yellow sample row if user left it
    if (values.every(v => v === null || v === undefined || String(v).trim() === '')) return
    const obj = {}
    headers.forEach((h, i) => {
      const raw = values[i]
      obj[h] = raw !== null && raw !== undefined ? String(raw).trim() : ''
    })
    rows.push(obj)
  })
  return { rows }
}

// ─── Row to API payload ────────────────────────────────────────────────────────

const rowToPayload = (row, deploymentType, emailId) => {
  const dependencies = row.dependencies
    ? row.dependencies.split('|').map(d => d.trim()).filter(Boolean)
    : []

  const payload = {
    common_name:       row.common_name,
    product_code:      row.product_code,
    product_name:      row.product_name || '',
    environment:       row.environment,
    email_id:          emailId,
    pre_existing:      'true',
    is_aws:            deploymentType === 'aws' ? 'true' : 'false',
    certificate_usage: row.certificate_usage,
    itsm_group:        row.itsm_group || '',
    venafi_policy_base:row.venafi_policy_base,
    san_entries:       [],
    mutual_auth:       row.mutual_auth || 'no',
    layer7:            row.layer7 || 'no',
    layer7_endpoint:   row.layer7_endpoint || null,
    client_involvement:row.client_name ? 'true' : 'false',
    client_name:       row.client_name || null,
    dependency_present:row.dependency_present || 'false',
    dependencies,
    aws_additional_fields: deploymentType === 'aws' ? {
      accountName:            row.account_name,
      accountNumber:          row.account_number,
      applicationId:          row.application_id,
      awsCertInOnprem:        row.aws_cert_in_onprem || 'no',
      acmParameter:           row.acm_parameter || 'no',
      existingAcmArn1:        row.existing_acm_arn1 || '',
      existingAcmArn2:        row.existing_acm_arn2 || '',
      ssmParameter:           row.ssm_parameter || 'no',
      ssmFormat:              row.ssm_format || '',
      ssmCertPath:            row.ssm_cert_path || (row.common_name && row.environment && row.ssm_format ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/${row.ssm_format.toLowerCase()}/cert` : ''),
      ssmCertContentType:     row.ssm_cert_content_type || 'String',
      ssmCertParamType:       row.ssm_cert_param_type || (row.ssm_format === 'PFX' ? 'Advanced' : 'Standard'),
      ssmPrivateKeyPath:      row.ssm_private_key_path || (row.ssm_format === 'PEM' && row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/pem/key` : ''),
      ssmKeyContentType:      row.ssm_key_content_type || (row.ssm_format === 'PEM' ? 'String' : ''),
      ssmKeyParamType:        row.ssm_key_param_type || (row.ssm_format === 'PEM' ? 'Standard' : ''),
      ssmChainKeyPath:        row.ssm_chain_key_path || (row.ssm_format === 'PEM' && row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/pem/chain` : ''),
      ssmChainContentType:    row.ssm_chain_content_type || (row.ssm_format === 'PEM' ? 'String' : ''),
      ssmChainParamType:      row.ssm_chain_param_type || (row.ssm_format === 'PEM' ? 'Advanced' : ''),
      ssmCertPasswordPath:    row.ssm_cert_password_path || (row.ssm_format === 'PFX' && row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}/pfx/password` : ''),
      ssmPasswordContentType: row.ssm_password_content_type || (row.ssm_format === 'PFX' ? 'String' : ''),
      ssmPasswordParamType:   row.ssm_password_param_type || (row.ssm_format === 'PFX' ? 'Standard' : ''),
      smParameter:            row.sm_parameter || 'no',
      smFormat:               row.sm_format || '',
      smCertPath:             row.sm_cert_path || (row.common_name && row.environment ? `/venafi/certificates/${row.environment.toLowerCase()}/${row.common_name}` : ''),
    } : null,
  }

  return payload
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const StatusChip = ({ row }) => {
  if (!row._validation) return null
  if (row._validation.errors.length > 0)
    return <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" variant="filled" />
  if (row._validation.warnings.length > 0)
    return <Chip icon={<WarningIcon />} label="Warning" color="warning" size="small" variant="outlined" />
  return <Chip icon={<CheckCircleIcon />} label="Valid" color="success" size="small" variant="outlined" />
}

const SubmitStatusChip = ({ status }) => {
  if (status === 'pending')   return <Chip label="Pending" color="default" size="small" />
  if (status === 'running')   return <Chip label="Submitting..." color="info" size="small" />
  if (status === 'success')   return <Chip icon={<CheckCircleIcon />} label="Success" color="success" size="small" />
  if (status === 'error')     return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
  return null
}

// ─── Main Component ────────────────────────────────────────────────────────────

const AWS_STEPS    = ['Select Type', 'Download Template', 'Upload File', 'Review & Validate', 'Submit']
const ONPREM_STEPS = ['Select Type', 'Download Template', 'Upload File', 'Venafi Lookup', 'Review & Validate', 'Submit']

const BulkCertificateUpload = () => {
  const [activeStep, setActiveStep]         = useState(0)
  const [deploymentType, setDeploymentType] = useState('')   // 'aws' | 'onprem'
  const [parsedRows, setParsedRows]         = useState([])
  const [parseError, setParseError]         = useState('')
  const [fileName, setFileName]             = useState('')
  const [expandedRows, setExpandedRows]     = useState({})
  const [submitting, setSubmitting]         = useState(false)
  const [submitResults, setSubmitResults]   = useState([]) // per-row {status, message}
  const [submitProgress, setSubmitProgress] = useState(0)
  const [columnDialogOpen, setColumnDialogOpen] = useState(false)
  const [snackbar, setSnackbar]             = useState({ open: false, message: '', severity: 'success' })
  const [dragOver, setDragOver]             = useState(false)

  // ─── Venafi lookup state (on-prem only) ──────────────────────────────────
  const [venafiLookupStatus, setVenafiLookupStatus]   = useState('idle') // 'idle' | 'running' | 'done'
  const [venafiLookupProgress, setVenafiLookupProgress] = useState(0)
  const [venafiMatches, setVenafiMatches]             = useState({})   // { [rowId]: { matches: [], status, error, selectedMatch, details } }
  const [certSelectionDialog, setCertSelectionDialog] = useState({ open: false, rowId: null }) // for multi-match picker
  const fileInputRef = useRef(null)

  const emailId = localStorage.getItem('mail') || ''

  const handleDeploymentSelect = (type) => {
    setDeploymentType(type)
  }

  const processFile = useCallback(async (file) => {
    if (!file) return
    const isCSV  = file.name.toLowerCase().endsWith('.csv')
    const isXLSX = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')
    if (!isCSV && !isXLSX) {
      setParseError('Please upload a .csv or .xlsx file.')
      return
    }
    setParseError('')
    setFileName(file.name)

    try {
      let rows
      if (isXLSX) {
        const result = await parseXLSXFile(file)
        rows = result.rows
      } else {
        const text = await file.text()
        const result = parseCSV(text)
        rows = result.rows
      }

      if (rows.length === 0) {
        setParseError('No data rows found. Ensure the file has a header row and at least one data row.')
        setParsedRows([])
        return
      }

      const validated = rows.map((row, idx) => {
        const normalized = normalizeRow(row)
        return {
          ...normalized,
          _id: idx,
          _validation: validateRow(normalized, deploymentType),
          _submitStatus: 'pending',
          _submitMessage: '',
        }
      })
      setParsedRows(validated)
      setSubmitResults([])
      // For on-prem, go to Venafi Lookup step (step 3); for AWS, go to Review (step 3 in AWS_STEPS)
      setActiveStep(3)
    } catch (err) {
      setParseError(`Failed to parse file: ${err.message}`)
      setParsedRows([])
    }
  }, [deploymentType])

  const handleFileInput = (e) => {
    const file = e.target.files[0]
    if (file) processFile(file)
    e.target.value = ''
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) processFile(file)
  }, [processFile])

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true) }
  const handleDragLeave = () => setDragOver(false)

  const toggleRowExpand = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }))

  const removeRow = (id) => setParsedRows(prev => prev.filter(r => r._id !== id))

  const STEPS = deploymentType === 'onprem' ? ONPREM_STEPS : AWS_STEPS
  // Step index mapping: on-prem has an extra "Venafi Lookup" step at index 3
  const REVIEW_STEP = deploymentType === 'onprem' ? 4 : 3
  const SUBMIT_STEP = deploymentType === 'onprem' ? 5 : 4

  const validRows    = parsedRows.filter(r => r._validation?.isValid)
  const invalidRows  = parsedRows.filter(r => !r._validation?.isValid)
  const warningRows  = parsedRows.filter(r => r._validation?.isValid && r._validation?.warnings.length > 0)

  // ─── Venafi Lookup Logic (On-Prem) ──────────────────────────────────────────

  const enrichRowFromVenafi = (row, details, match) => {
    const enriched = { ...row }
    // Only fill fields that the user did NOT provide in their CSV
    if (!enriched.environment && details.environment)
      enriched.environment = details.environment.toLowerCase()
    if (!enriched.certificate_usage && details.certificate_usage)
      enriched.certificate_usage = details.certificate_usage
    if (!enriched.venafi_policy_base) {
      enriched.venafi_policy_base = detectPolicyBase(match.policy_dn || match.certificate_dn)
    }
    if (!enriched.client_name && details.client_name)
      enriched.client_name = details.client_name
    // Set defaults for fields that weren't provided
    if (!enriched.mutual_auth) enriched.mutual_auth = 'no'
    if (!enriched.dependency_present) enriched.dependency_present = 'false'
    // Store Venafi metadata for display
    enriched._venafiDetails = details
    enriched._venafiMatch = match
    return enriched
  }

  const runVenafiLookup = async () => {
    setVenafiLookupStatus('running')
    setVenafiLookupProgress(0)
    const newMatches = {}
    let done = 0
    const total = parsedRows.length

    // Process rows in batches of 5 to avoid overwhelming the API
    const BATCH_SIZE = 5
    for (let i = 0; i < parsedRows.length; i += BATCH_SIZE) {
      const batch = parsedRows.slice(i, i + BATCH_SIZE)
      const results = await Promise.allSettled(
        batch.map(async (row) => {
          const cn = row.common_name?.trim()
          if (!cn) {
            return { rowId: row._id, status: 'error', error: 'No common_name provided', matches: [] }
          }
          try {
            // Check if already onboarded
            const onboardedResp = await certificateApi.checkOnboarded(cn)
            if (onboardedResp.data?.onboarded) {
              return {
                rowId: row._id,
                status: 'already-onboarded',
                error: `Already onboarded (ID: ${onboardedResp.data.cert_id || 'N/A'})`,
                matches: [],
              }
            }
            // Search Venafi
            const venafiResp = await certificateApi.checkVenafiExists(cn, 25)
            const { exists, matches, truncated } = venafiResp.data || {}
            if (!exists || !matches?.length) {
              return { rowId: row._id, status: 'not-found', error: 'Certificate not found in Venafi', matches: [] }
            }
            return {
              rowId: row._id,
              status: matches.length === 1 ? 'single' : 'multiple',
              matches: matches,
              truncated: Boolean(truncated),
              error: '',
            }
          } catch (err) {
            return {
              rowId: row._id,
              status: 'error',
              error: err?.response?.data?.detail || err?.message || 'Venafi lookup failed',
              matches: [],
            }
          }
        })
      )
      results.forEach((outcome) => {
        if (outcome.status === 'fulfilled') {
          const r = outcome.value
          newMatches[r.rowId] = r
        }
      })
      done += batch.length
      setVenafiLookupProgress(Math.round((done / total) * 100))
      setVenafiMatches({ ...newMatches })
    }

    // Auto-fetch details for single-match rows
    const singleMatchRows = Object.values(newMatches).filter(m => m.status === 'single')
    for (let i = 0; i < singleMatchRows.length; i += BATCH_SIZE) {
      const batch = singleMatchRows.slice(i, i + BATCH_SIZE)
      await Promise.allSettled(
        batch.map(async (m) => {
          const match = m.matches[0]
          try {
            const detResp = await certificateApi.getVenafiCertificateDetails(match.certificate_dn, match.guid)
            newMatches[m.rowId] = { ...newMatches[m.rowId], selectedMatch: match, details: detResp.data || {} }
          } catch (err) {
            newMatches[m.rowId] = {
              ...newMatches[m.rowId],
              status: 'error',
              error: `Found in Venafi but failed to fetch details: ${err?.message || 'unknown error'}`,
            }
          }
        })
      )
      setVenafiMatches({ ...newMatches })
    }

    setVenafiMatches({ ...newMatches })
    setVenafiLookupStatus('done')
  }

  const handleSelectVenafiCert = async (rowId, match) => {
    // Update the selection immediately
    setVenafiMatches(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], selectedMatch: match, details: null, status: 'fetching-details' },
    }))
    try {
      const detResp = await certificateApi.getVenafiCertificateDetails(match.certificate_dn, match.guid)
      setVenafiMatches(prev => ({
        ...prev,
        [rowId]: { ...prev[rowId], details: detResp.data || {}, status: 'multiple' },
      }))
    } catch (err) {
      setVenafiMatches(prev => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          status: 'error',
          error: `Failed to fetch certificate details: ${err?.message || 'unknown error'}`,
        },
      }))
    }
    setCertSelectionDialog({ open: false, rowId: null })
  }

  const applyVenafiEnrichmentAndProceed = () => {
    // Merge Venafi data into parsed rows, re-validate, then go to Review step
    const enriched = parsedRows.map(row => {
      const lookupResult = venafiMatches[row._id]
      if (!lookupResult) return row
      if (lookupResult.status === 'already-onboarded') {
        return {
          ...row,
          _validation: { errors: [lookupResult.error], warnings: [], isValid: false },
          _venafiStatus: 'already-onboarded',
        }
      }
      if (lookupResult.status === 'not-found' || lookupResult.status === 'error') {
        return {
          ...row,
          _validation: { errors: [lookupResult.error], warnings: [], isValid: false },
          _venafiStatus: lookupResult.status,
        }
      }
      if (!lookupResult.selectedMatch || !lookupResult.details) {
        return {
          ...row,
          _validation: { errors: ['No Venafi certificate selected'], warnings: [], isValid: false },
          _venafiStatus: 'no-selection',
        }
      }
      // Enrich from Venafi
      const enrichedRow = enrichRowFromVenafi(row, lookupResult.details, lookupResult.selectedMatch)
      const normalized = normalizeRow(enrichedRow)
      return {
        ...normalized,
        _id: row._id,
        _validation: validateRowPostEnrichment(normalized),
        _submitStatus: 'pending',
        _submitMessage: '',
        _venafiStatus: 'enriched',
      }
    })
    setParsedRows(enriched)
    setActiveStep(REVIEW_STEP)
  }

  // Check if all multi-match rows have a selection
  const allMultiMatchesResolved = Object.values(venafiMatches).every(
    m => m.status !== 'multiple' || (m.selectedMatch && m.details)
  )

  const handleSubmitAll = async () => {
    if (validRows.length === 0) return
    setSubmitting(true)
    setSubmitProgress(0)
    setActiveStep(SUBMIT_STEP)

    const results = parsedRows.map(r => ({ ...r, _submitStatus: r._validation.isValid ? 'pending' : 'skipped', _submitMessage: '' }))
    setSubmitResults([...results])

    // Pre-flight: check all valid rows against the DB in parallel to skip already-onboarded certs
    const duplicateChecks = await Promise.allSettled(
      results.map(async (row, i) => {
        if (!row._validation.isValid) return null
        try {
          const resp = await certificateApi.checkOnboarded(row.common_name)
          return resp.data?.onboarded ? i : null
        } catch {
          return null
        }
      })
    )
    duplicateChecks.forEach((outcome, i) => {
      if (outcome.status === 'fulfilled' && outcome.value !== null) {
        results[i] = {
          ...results[i],
          _submitStatus: 'skipped',
          _submitMessage: `Already onboarded — skipped.`,
        }
      }
    })
    setSubmitResults([...results])

    let done = 0
    const total = results.filter(r => r._submitStatus === 'pending').length

    for (let i = 0; i < results.length; i++) {
      const row = results[i]
      if (row._submitStatus !== 'pending') continue

      results[i] = { ...results[i], _submitStatus: 'running' }
      setSubmitResults([...results])

      try {
        const payload = rowToPayload(row, deploymentType, emailId)
        await certificateApi.onboard(payload)
        results[i] = { ...results[i], _submitStatus: 'success', _submitMessage: 'Certificate onboarded successfully' }
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Submission failed'
        results[i] = { ...results[i], _submitStatus: 'error', _submitMessage: msg }
      }

      done++
      setSubmitProgress(Math.round((done / total) * 100))
      setSubmitResults([...results])
    }

    setSubmitting(false)
    const successCount = results.filter(r => r._submitStatus === 'success').length
    const skippedCount = results.filter(r => r._submitStatus === 'skipped').length
    const failCount    = results.filter(r => r._submitStatus === 'error').length
    setSnackbar({
      open: true,
      message: `Bulk upload complete: ${successCount} succeeded, ${failCount} failed${skippedCount > 0 ? `, ${skippedCount} skipped (already onboarded)` : ''}.`,
      severity: failCount === 0 ? 'success' : 'warning'
    })
  }

  const handleReset = () => {
    setActiveStep(0)
    setDeploymentType('')
    setParsedRows([])
    setParseError('')
    setFileName('')
    setExpandedRows({})
    setSubmitting(false)
    setSubmitResults([])
    setSubmitProgress(0)
    setVenafiLookupStatus('idle')
    setVenafiLookupProgress(0)
    setVenafiMatches({})
    setCertSelectionDialog({ open: false, rowId: null })
  }

  // ─── Render Steps ────────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
        Select Deployment Target
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose where these pre-existing certificates will be deployed. All certificates in your upload will share the same deployment type.
      </Typography>
      <Grid container spacing={3}>
        {[
          {
            type: 'aws',
            icon: <CloudIcon sx={{ fontSize: 56 }} />,
            title: 'AWS',
            subtitle: 'Certificate stored in ACM, SSM, or Secrets Manager',
            color: 'primary',
          },
          {
            type: 'onprem',
            icon: <StorageIcon sx={{ fontSize: 56 }} />,
            title: 'On-Premises',
            subtitle: 'Certificate deployed to on-premises infrastructure via Venafi',
            color: 'secondary',
          },
        ].map(({ type, icon, title, subtitle, color }) => (
          <Grid item xs={12} sm={6} key={type}>
            <Paper
              elevation={deploymentType === type ? 6 : 1}
              onClick={() => handleDeploymentSelect(type)}
              sx={{
                p: 4,
                cursor: 'pointer',
                border: deploymentType === type ? '2.5px solid' : '1.5px solid',
                borderColor: deploymentType === type ? `${color}.main` : 'grey.200',
                borderRadius: 3,
                textAlign: 'center',
                transition: 'all 0.2s ease',
                bgcolor: deploymentType === type ? `${color}.50` : 'background.paper',
                '&:hover': {
                  borderColor: `${color}.main`,
                  bgcolor: `${color}.50`,
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                },
              }}
            >
              <Box sx={{ color: deploymentType === type ? `${color}.main` : 'grey.400', mb: 2 }}>
                {icon}
              </Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
              {deploymentType === type && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Selected"
                  color={color}
                  size="small"
                  sx={{ mt: 2 }}
                />
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          disabled={!deploymentType}
          onClick={() => setActiveStep(1)}
          sx={{ px: 4 }}
        >
          Next: Get Template
        </Button>
      </Box>
    </Box>
  )

  const renderStep1 = () => {
    const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
    const requiredCols = cols.filter(c => c.required)
    const optionalCols = cols.filter(c => !c.required)

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Download CSV Template
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          Download the template for <strong>{deploymentType === 'aws' ? 'AWS' : 'On-Premises'}</strong> bulk onboarding.
          {deploymentType === 'onprem' ? (
            <> Only <strong>common_name</strong>, <strong>product_code</strong>, and <strong>product_name</strong> are required.
            All other fields (environment, certificate usage, SANs, policy base, etc.) will be <strong>automatically populated from Venafi</strong>.
            You can still provide overrides in the template if needed.</>
          ) : (
            <> Fill in your certificate data, then upload the completed file in the next step.</>
          )}
          {' '}The template includes one sample row to guide you.
        </Alert>

        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ArticleIcon color="primary" sx={{ fontSize: 48 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    bulk_upload_{deploymentType}_template.csv
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cols.length} columns · {requiredCols.length} required · 1 sample row
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                size="large"
                onClick={() => generateXLSXTemplate(deploymentType)}
              >
                Download Template (.xlsx)
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<InfoIcon />} onClick={() => setColumnDialogOpen(true)}>
            View All {cols.length} Column Definitions
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: 'error.main' }}>
                  Required Columns ({requiredCols.length})
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {requiredCols.map(c => (
                    <Box key={c.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label={c.key} size="small" color="error" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', height: 22 }} />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Optional Columns ({optionalCols.length})
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {optionalCols.map(c => (
                    <Box key={c.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip label={c.key} size="small" variant="outlined" color="default" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', height: 22, color: 'text.secondary' }} />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2" gutterBottom fontWeight={600}>Tips before uploading:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {deploymentType === 'onprem' && (
              <li>Only <strong>common_name, product_code, product_name</strong> are required — all other fields are auto-populated from Venafi</li>
            )}
            <li>Use <strong>pipe characters (|)</strong> to separate multiple SAN entries or dependencies within a single cell</li>
            <li>All certificates in the file are treated as <strong>pre-existing</strong></li>
            <li>Leave optional fields blank; do not remove their columns</li>
            <li>Upload the <strong>.xlsx</strong> directly, or export from Excel as <strong>CSV UTF-8</strong></li>
            <li>Enum fields (dropdowns) are <strong>case-insensitive</strong> — values are normalised automatically on upload</li>
            {deploymentType === 'onprem' && (
              <li>If multiple certificates share the same common name, you will be prompted to select the correct one</li>
            )}
          </ul>
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" onClick={() => setActiveStep(0)}>Back</Button>
          <Button variant="contained" size="large" onClick={() => setActiveStep(2)} sx={{ px: 4 }}>
            Next: Upload File
          </Button>
        </Box>

        {/* Column Reference Dialog */}
        <Dialog open={columnDialogOpen} onClose={() => setColumnDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Column Reference — {deploymentType === 'aws' ? 'AWS' : 'On-Premises'}
            <Typography variant="caption" display="block" color="text.secondary">
              {cols.length} total columns
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Column</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cols.map(c => (
                    <TableRow key={c.key} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{c.key}</TableCell>
                      <TableCell>
                        {c.required
                          ? <Chip label="Required" color="error" size="small" variant="outlined" />
                          : <Chip label="Optional" color="default" size="small" variant="outlined" />
                        }
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{c.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setColumnDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
        Upload Your File
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload the completed file (.xlsx or .csv). The file will be validated instantly and errors will be highlighted.
        Enum field values are automatically normalised — case does not matter.
      </Typography>

      {/* Drop Zone */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: `2.5px dashed`,
          borderColor: dragOver ? 'primary.main' : 'grey.300',
          borderRadius: 3,
          p: 6,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: dragOver ? 'primary.50' : 'grey.50',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'primary.50',
          },
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 64, color: dragOver ? 'primary.main' : 'grey.400', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {dragOver ? 'Drop your file here' : 'Drag & drop your .xlsx or .csv file here'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          or click to browse
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
          <Chip label=".xlsx" size="small" color="primary" variant="outlined" />
          <Chip label=".csv" size="small" variant="outlined" />
        </Stack>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
      </Box>

      {fileName && !parseError && (
        <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
          <strong>{fileName}</strong> loaded successfully — {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} found.
          {deploymentType === 'onprem'
            ? ' Click "Venafi Lookup" to proceed.'
            : ' Click "Review Data" to proceed.'}
        </Alert>
      )}

      {parseError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {parseError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="outlined" onClick={() => setActiveStep(1)}>Back</Button>
        <Button
          variant="contained"
          size="large"
          disabled={parsedRows.length === 0 || !!parseError}
          onClick={() => setActiveStep(3)}
          sx={{ px: 4 }}
        >
          {deploymentType === 'onprem'
            ? `Venafi Lookup (${parsedRows.length} rows)`
            : `Review Data (${parsedRows.length} rows)`}
        </Button>
      </Box>
    </Box>
  )

  // ─── Venafi Lookup Step (On-Prem only, step index 3) ──────────────────────
  const renderStepVenafiLookup = () => {
    const numTotal     = parsedRows.length
    const numSingle    = Object.values(venafiMatches).filter(m => m.status === 'single' && m.details).length
    const numMultiple  = Object.values(venafiMatches).filter(m => m.status === 'multiple' || m.status === 'fetching-details').length
    const numResolved  = Object.values(venafiMatches).filter(m => (m.status === 'multiple' || m.status === 'fetching-details') && m.selectedMatch && m.details).length
    const numError     = Object.values(venafiMatches).filter(m => m.status === 'error' || m.status === 'not-found' || m.status === 'already-onboarded').length

    // parseDN utility
    const parseDN = (dn) => {
      const parts = dn.replace(/\\\\/g, '\\').split('\\').filter(Boolean)
      const cn = parts[parts.length - 1] || dn
      const isProd = !dn.toLowerCase().includes('non-production') && dn.toLowerCase().includes('production')
      const folder = parts.slice(0, -1).join(' \u203A ')
      // Detect CA from path
      let ca = ''
      if (dn.toLowerCase().includes('digicert')) ca = 'DigiCert'
      else if (dn.toLowerCase().includes('entrust')) ca = 'Entrust'
      return { cn, isProd, folder, ca }
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          <SearchIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Venafi Certificate Lookup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each certificate will be looked up in Venafi by its common name. Fields like environment, certificate usage,
          SANs, and policy base will be automatically populated from Venafi data.
        </Typography>

        {venafiLookupStatus === 'idle' && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Click <strong>Start Venafi Lookup</strong> to search for all {numTotal} certificate{numTotal !== 1 ? 's' : ''} in Venafi.
              If multiple certificates share the same common name, you&apos;ll be asked to choose.
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button variant="outlined" onClick={() => setActiveStep(2)}>Back</Button>
              <Button variant="contained" size="large" startIcon={<SearchIcon />} onClick={runVenafiLookup} sx={{ px: 4 }}>
                Start Venafi Lookup ({numTotal} certificates)
              </Button>
            </Box>
          </>
        )}

        {venafiLookupStatus === 'running' && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Searching Venafi for certificates...
              </Typography>
              <Typography variant="body2" fontWeight={600}>{venafiLookupProgress}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={venafiLookupProgress} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        )}

        {(venafiLookupStatus === 'running' || venafiLookupStatus === 'done') && (
          <>
            {/* Summary chips */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
              <Chip icon={<CheckCircleIcon />} label={`${numSingle} Auto-matched`} color="success" size="small" />
              {numMultiple > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${numMultiple} Need selection (${numResolved} resolved)`}
                  color={numResolved === numMultiple ? 'success' : 'warning'}
                  size="small"
                />
              )}
              {numError > 0 && <Chip icon={<ErrorIcon />} label={`${numError} Failed`} color="error" size="small" />}
            </Box>

            {/* Row-by-row results */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3, maxHeight: 500, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 40 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Common Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 160 }}>Venafi Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Selected Certificate</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 100 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedRows.map((row, idx) => {
                    const lookup = venafiMatches[row._id]
                    const isLoading = !lookup
                    const isSingle = lookup?.status === 'single' && lookup?.details
                    const isMultiple = lookup?.status === 'multiple' || lookup?.status === 'fetching-details'
                    const hasSelection = isMultiple && lookup?.selectedMatch && lookup?.details
                    const isError = lookup?.status === 'error' || lookup?.status === 'not-found' || lookup?.status === 'already-onboarded'

                    let statusChip
                    if (isLoading) statusChip = <Chip label="Waiting..." size="small" color="default" />
                    else if (isSingle) statusChip = <Chip icon={<CheckCircleIcon />} label="Auto-matched" size="small" color="success" />
                    else if (isMultiple && hasSelection) statusChip = <Chip icon={<CheckCircleIcon />} label="Selected" size="small" color="success" />
                    else if (isMultiple) statusChip = <Chip icon={<WarningIcon />} label={`${lookup.matches.length} matches`} size="small" color="warning" />
                    else if (lookup?.status === 'already-onboarded') statusChip = <Chip icon={<ErrorIcon />} label="Already Onboarded" size="small" color="error" variant="outlined" />
                    else if (isError) statusChip = <Chip icon={<ErrorIcon />} label="Not Found" size="small" color="error" />
                    else statusChip = <Chip label="..." size="small" />

                    const selectedDn = lookup?.selectedMatch?.certificate_dn || lookup?.selectedMatch?.policy_dn
                    const selectedParsed = selectedDn ? parseDN(selectedDn) : null

                    return (
                      <TableRow
                        key={row._id}
                        sx={{
                          bgcolor: isError ? 'error.50' : (isMultiple && !hasSelection) ? 'warning.50' : 'inherit',
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{row.common_name}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{row.product_code}</TableCell>
                        <TableCell>{statusChip}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>
                          {(isSingle || hasSelection) && selectedParsed ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LockIcon sx={{ fontSize: 14, color: 'success.main' }} />
                              <Box>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                                  {selectedParsed.cn}
                                </Typography>
                                {selectedParsed.ca && (
                                  <Chip label={selectedParsed.ca} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                                )}
                                {selectedParsed.isProd !== undefined && (
                                  <Chip
                                    label={selectedParsed.isProd ? 'PRD' : 'NON-PRD'}
                                    size="small"
                                    sx={{ height: 16, fontSize: '0.6rem', ml: 0.5,
                                      bgcolor: selectedParsed.isProd ? 'error.dark' : 'info.dark', color: '#fff' }}
                                  />
                                )}
                              </Box>
                            </Box>
                          ) : isError ? (
                            <Typography variant="caption" color="error.main">{lookup?.error}</Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {isMultiple && !hasSelection && (
                            <Button
                              size="small"
                              variant="contained"
                              color="warning"
                              onClick={() => setCertSelectionDialog({ open: true, rowId: row._id })}
                            >
                              Choose
                            </Button>
                          )}
                          {isMultiple && hasSelection && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setCertSelectionDialog({ open: true, rowId: row._id })}
                            >
                              Change
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {venafiLookupStatus === 'done' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant="outlined" onClick={() => { setVenafiLookupStatus('idle'); setVenafiMatches({}); setActiveStep(2) }}>
                    Re-upload File
                  </Button>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={runVenafiLookup}>
                    Re-run Lookup
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!allMultiMatchesResolved}
                  onClick={applyVenafiEnrichmentAndProceed}
                  sx={{ px: 4 }}
                >
                  {allMultiMatchesResolved
                    ? `Continue to Review (${numSingle + numResolved} enriched)`
                    : `Resolve ${numMultiple - numResolved} remaining selection${numMultiple - numResolved !== 1 ? 's' : ''}`
                  }
                </Button>
              </Box>
            )}
          </>
        )}

        {/* ─── Certificate Selection Dialog (for multi-match rows) ───────────── */}
        <Dialog
          open={certSelectionDialog.open}
          onClose={() => setCertSelectionDialog({ open: false, rowId: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Select Certificate for Row {certSelectionDialog.rowId !== null
              ? parsedRows.findIndex(r => r._id === certSelectionDialog.rowId) + 1
              : ''}
            <Typography variant="caption" display="block" color="text.secondary">
              {certSelectionDialog.rowId !== null && parsedRows.find(r => r._id === certSelectionDialog.rowId)?.common_name}
              {' — '}
              {venafiMatches[certSelectionDialog.rowId]?.matches?.length || 0} certificates found in Venafi
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {certSelectionDialog.rowId !== null && venafiMatches[certSelectionDialog.rowId]?.matches?.map((match, idx) => {
              const parsed = parseDN(match.certificate_dn || match.policy_dn || '')
              const isSelected = venafiMatches[certSelectionDialog.rowId]?.selectedMatch?.guid === match.guid
                || venafiMatches[certSelectionDialog.rowId]?.selectedMatch?.certificate_dn === match.certificate_dn
              const isFetching = isSelected && venafiMatches[certSelectionDialog.rowId]?.status === 'fetching-details'

              return (
                <Paper
                  key={`${match.guid || match.certificate_dn}-${idx}`}
                  elevation={isSelected ? 4 : 1}
                  sx={{
                    mb: 1.5,
                    border: '2px solid',
                    borderColor: isSelected ? 'success.main' : 'grey.200',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: isSelected ? 'success.main' : 'primary.main', boxShadow: 3 },
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2, py: 1.5,
                      background: isSelected
                        ? 'linear-gradient(90deg, #1b5e20 0%, #2e7d32 100%)'
                        : parsed.isProd
                          ? 'linear-gradient(90deg, #1a2c50 0%, #243858 100%)'
                          : 'linear-gradient(90deg, #1a3050 0%, #1f3a60 100%)',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                    }}
                  >
                    <LockIcon sx={{ color: isSelected ? '#a5d6a7' : '#90caf9', fontSize: 18, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        color: '#fff', fontWeight: 600, fontSize: '0.85rem', flex: 1,
                        fontFamily: '"Roboto Mono","Courier New",monospace',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {parsed.cn}
                    </Typography>
                    <Chip
                      label={parsed.isProd ? 'PRD' : 'NON-PRD'}
                      size="small"
                      sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700,
                        bgcolor: parsed.isProd ? 'error.dark' : 'info.dark', color: '#fff', flexShrink: 0 }}
                    />
                    {parsed.ca && (
                      <Chip label={parsed.ca} size="small"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                    )}
                    {isSelected && <CheckCircleIcon sx={{ color: '#a5d6a7', fontSize: 18, flexShrink: 0 }} />}
                  </Box>

                  {/* Body */}
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      <strong>DN:</strong> {match.certificate_dn || match.policy_dn}
                    </Typography>
                    {match.guid && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>GUID:</strong> {match.guid}
                      </Typography>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.100', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant={isSelected ? 'contained' : 'outlined'}
                      color={isSelected ? 'success' : 'primary'}
                      size="small"
                      disabled={isFetching}
                      onClick={() => !isSelected && handleSelectVenafiCert(certSelectionDialog.rowId, match)}
                      startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : null}
                      sx={{ minWidth: 120 }}
                    >
                      {isFetching ? 'Fetching details…' : isSelected ? 'Selected ✓' : 'Select'}
                    </Button>
                  </Box>
                </Paper>
              )
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCertSelectionDialog({ open: false, rowId: null })}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  }

  const renderStep3 = () => {
    const cols = deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS
    const visibleCols = cols.slice(0, deploymentType === 'aws' ? 8 : 6) // Show first N cols in table

    return (
      <Box>
        {/* Summary Bar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Review & Validate
          </Typography>
          <Chip icon={<CheckCircleIcon />} label={`${validRows.length} Valid`} color="success" />
          {warningRows.length > 0 && <Chip icon={<WarningIcon />} label={`${warningRows.length} Warnings`} color="warning" />}
          {invalidRows.length > 0 && <Chip icon={<ErrorIcon />} label={`${invalidRows.length} Errors`} color="error" />}
          <Chip label={`${parsedRows.length} Total`} color="default" />
        </Box>

        {invalidRows.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>{invalidRows.length} row{invalidRows.length > 1 ? 's have' : ' has'} validation errors</strong> and will be skipped during submission.
            Expand each row to see the specific errors. Fix them in your CSV and re-upload, or remove the rows.
          </Alert>
        )}

        {invalidRows.length === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            All {validRows.length} rows passed validation. Ready to submit.
          </Alert>
        )}

        {deploymentType === 'onprem' && invalidRows.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fields marked with a <Chip label="Venafi" size="small" color="info" variant="outlined" sx={{ mx: 0.5, height: 18, fontSize: '0.65rem' }} /> badge
            were auto-populated from Venafi certificate data. Expand rows to see all enriched values.
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3, maxHeight: 500, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 100 }}>Status</TableCell>
                {visibleCols.map(c => (
                  <TableCell key={c.key} sx={{ fontWeight: 700, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {c.key}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsedRows.map((row, idx) => (
                <React.Fragment key={row._id}>
                  <TableRow
                    hover
                    sx={{
                      bgcolor: row._validation?.errors.length > 0
                        ? 'error.50'
                        : row._validation?.warnings.length > 0
                          ? 'warning.50'
                          : 'inherit',
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{idx + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StatusChip row={row} />
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpand(row._id)}
                          sx={{ ml: 0.5 }}
                        >
                          {expandedRows[row._id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    </TableCell>
                    {visibleCols.map(c => (
                      <TableCell
                        key={c.key}
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.78rem',
                          maxWidth: 160,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={row[c.key] || '—'} arrow>
                          <span>{row[c.key] || <span style={{ color: '#aaa' }}>—</span>}</span>
                        </Tooltip>
                      </TableCell>
                    ))}
                    <TableCell>
                      <Tooltip title="Remove row">
                        <IconButton size="small" color="error" onClick={() => removeRow(row._id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Expanded details row */}
                  <TableRow>
                    <TableCell colSpan={visibleCols.length + 3} sx={{ py: 0, borderBottom: expandedRows[row._id] ? undefined : 'none' }}>
                      <Collapse in={!!expandedRows[row._id]} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 3, bgcolor: 'grey.50', borderRadius: 1, mx: 1, my: 1 }}>
                          <Grid container spacing={2}>
                            {/* Errors */}
                            {row._validation?.errors.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="error.main" fontWeight={700} gutterBottom display="block">
                                  Errors ({row._validation.errors.length})
                                </Typography>
                                <List dense disablePadding>
                                  {row._validation.errors.map((e, i) => (
                                    <ListItem key={i} disablePadding>
                                      <ErrorIcon color="error" sx={{ fontSize: 14, mr: 1 }} />
                                      <ListItemText primary={e} primaryTypographyProps={{ variant: 'caption', color: 'error.main' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </Grid>
                            )}
                            {/* Warnings */}
                            {row._validation?.warnings.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <Typography variant="caption" color="warning.dark" fontWeight={700} gutterBottom display="block">
                                  Warnings ({row._validation.warnings.length})
                                </Typography>
                                <List dense disablePadding>
                                  {row._validation.warnings.map((w, i) => (
                                    <ListItem key={i} disablePadding>
                                      <WarningIcon color="warning" sx={{ fontSize: 14, mr: 1 }} />
                                      <ListItemText primary={w} primaryTypographyProps={{ variant: 'caption', color: 'warning.dark' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </Grid>
                            )}
                            {/* All field values */}
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" gutterBottom>
                                All Field Values
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {(deploymentType === 'aws' ? AWS_COLUMNS : ONPREM_COLUMNS).map(c => (
                                  row[c.key] ? (
                                    <Chip
                                      key={c.key}
                                      label={`${c.key}: ${row[c.key]}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }}
                                    />
                                  ) : null
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => setActiveStep(deploymentType === 'onprem' ? 3 : 2)}>Back</Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => { setParsedRows([]); setFileName(''); setVenafiMatches({}); setVenafiLookupStatus('idle'); setActiveStep(2) }}
            >
              Re-upload File
            </Button>
          </Box>
          <Button
            variant="contained"
            color="success"
            size="large"
            disabled={validRows.length === 0}
            startIcon={<SendIcon />}
            onClick={handleSubmitAll}
            sx={{ px: 4 }}
          >
            Submit {validRows.length} Certificate{validRows.length !== 1 ? 's' : ''}
            {invalidRows.length > 0 && ` (skip ${invalidRows.length} invalid)`}
          </Button>
        </Box>
      </Box>
    )
  }

  const renderStep4 = () => {
    const successCount = submitResults.filter(r => r._submitStatus === 'success').length
    const failCount    = submitResults.filter(r => r._submitStatus === 'error').length
    const pendingCount = submitResults.filter(r => r._submitStatus === 'pending' || r._submitStatus === 'running').length
    const total        = validRows.length

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          Bulk Submission Progress
        </Typography>

        {/* Progress Bar */}
        {submitting && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Submitting certificates...
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {submitProgress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={submitProgress} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
        )}

        {!submitting && (
          <Alert
            severity={failCount === 0 ? 'success' : failCount === total ? 'error' : 'warning'}
            sx={{ mb: 3 }}
            action={
              <Button size="small" onClick={handleReset} startIcon={<RefreshIcon />}>
                Start New Upload
              </Button>
            }
          >
            <Typography fontWeight={700}>
              {failCount === 0
                ? `All ${successCount} certificates onboarded successfully!`
                : `${successCount} succeeded, ${failCount} failed.`
              }
            </Typography>
          </Alert>
        )}

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {successCount > 0 && <Chip icon={<CheckCircleIcon />} label={`${successCount} Succeeded`} color="success" />}
          {failCount > 0 && <Chip icon={<ErrorIcon />} label={`${failCount} Failed`} color="error" />}
          {pendingCount > 0 && <Chip label={`${pendingCount} Pending`} color="default" />}
          {invalidRows.length > 0 && <Chip label={`${invalidRows.length} Skipped (invalid)`} color="warning" variant="outlined" />}
        </Box>

        {/* Results Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 450, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Common Name</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Environment</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 140 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submitResults.map((row, idx) => (
                <TableRow
                  key={row._id}
                  sx={{
                    bgcolor: row._submitStatus === 'success'
                      ? 'success.50'
                      : row._submitStatus === 'error'
                        ? 'error.50'
                        : 'inherit'
                  }}
                >
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.common_name}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{row.product_code}</TableCell>
                  <TableCell>
                    {row.environment && (
                      <Chip
                        label={(row.environment || '').toUpperCase()}
                        size="small"
                        color={row.environment === 'prd' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <SubmitStatusChip status={row._submitStatus} />
                    {row._submitStatus === 'skipped' && (
                      <Chip label="Skipped" color="warning" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', color: row._submitStatus === 'error' ? 'error.main' : 'text.secondary' }}>
                    {row._submitMessage || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!submitting && failCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              To retry failed rows, download the results and fix the errors, then re-upload only the failed rows.
            </Typography>
          </Alert>
        )}
      </Box>
    )
  }

  const stepContent = deploymentType === 'onprem'
    ? [renderStep0, renderStep1, renderStep2, renderStepVenafiLookup, renderStep3, renderStep4]
    : [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4]

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Badge
          badgeContent={parsedRows.length > 0 ? parsedRows.length : undefined}
          color="primary"
          max={999}
        >
          <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Badge>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Bulk Certificate Onboarding
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pre-existing certificates only — onboard multiple certificates at once via CSV upload
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.82rem',
                    fontWeight: index === activeStep ? 700 : 400,
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        {stepContent[activeStep]?.()}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={7000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default BulkCertificateUpload
